import { Accordion, Card, Grid, InputSelect, InputText } from "#/atoms";
import { Datepicker, Timepicker } from "#/molecules";
import { forwardRef, memo, useCallback, useImperativeHandle } from "react";

import { CardHandle } from "../Cards.interface";
import { DESKTOP_SPACING } from "@/theme";
import { DateAndTimeCardProps } from "./DateAndTime.interface";
import { HeadingIcon } from "#/molecules/HeadingIcon";

const DateAndTimeCard = forwardRef<CardHandle, DateAndTimeCardProps> (
  ({
  accordionState = 'open',
  appointmentDate,
  appointmentTime,
  appointmentDuration,
  contactVendorDirectly,
  isReadOnly,
  notifyViaSMS,
}, ref) => {
  const description = 'Please note that appointment dates & times requested are not guaranteed. We will need to confirm our availability when we allocate a photoplanner to the job.';

  useImperativeHandle(ref, useCallback(() => ({
    validate: () => {
      const isValid = contactVendorDirectly.checked || ((appointmentDate.value && appointmentTime.value && appointmentDuration.value) ? true : false );

      return { isValid: isValid,
        validationErrors: isValid ? [] : [ 'Please specify the Date and Time or tick a check-box \'Contact Vendor or Tenant\'']};
    }
  }), [contactVendorDirectly.checked, appointmentDate.value, appointmentTime.value, appointmentDuration.value]));
  
  return (
    <Card>
      <Accordion
        header={(
          <HeadingIcon
            description={accordionState === 'open' ? description : ''}
            icon="calendar"
            title="Date & Time"
          />
        )}
        headerMargin={`${DESKTOP_SPACING.XXXL}px`}
        showArrow
        state={accordionState}
      >
        <InputSelect
          type="checkbox"
          checked={contactVendorDirectly.checked}
          label="Contact Vendor or Tenant directly for the most convenient date and time."
          name="direct-contact"
          value="direct-contact"
          readOnly={isReadOnly}
          onChangeValue={contactVendorDirectly.onChange}
        />
        <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(2, 40%)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
          <Datepicker
            label="Appointment Date"
            date={appointmentDate.value}
            onChange={appointmentDate.onChange}
          />
          <InputText type="text" label="Timezone" disabled value="Europe/London" readOnly={isReadOnly} />
          <Timepicker
            label="Appointment Time"
            time={appointmentTime.value}
            onChange={appointmentTime.onChange}
          />
          <InputText
            type="number"
            label="Appointment Duration (minutes)"
            value={appointmentDuration.value}
            onChange={appointmentDuration.onChange}
            readOnly={isReadOnly}
          />
        </Grid>
        <InputSelect
          type="checkbox"
          checked={notifyViaSMS.checked}
          id="notify-sms"
          label="Notify about progress via SMS"
          name="notify-sms"
          value="notify-sms"
          onChangeValue={notifyViaSMS.onChange}
          readOnly={isReadOnly}
        />
      </Accordion>
    </Card>
  );
}
);

DateAndTimeCard.displayName = 'DateAndTimeCard';

export default memo(DateAndTimeCard);